import React, { useState, useMemo } from 'react';

const ClientSearchModal = ({ isOpen, onClose, onSelectClient, rawClientList = [], searchYear }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortKey, setSortKey] = useState('totalSales'); 
    const [sortOrder, setSortOrder] = useState('desc'); 

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortKey(key);
            setSortOrder('desc');
        }
    };

    // 🎯 검색 시 랭킹이 고정되도록 전체 목록(rawClientList) 기준 연산
    const processedClients = useMemo(() => {
        // 검색어 입력과 상관없이 '전체 목록(rawClientList)'을 기준으로 고정 등수 맵 생성
        const salesRankMap = [...rawClientList]
            .sort((a, b) => (Number(b.totalSales) || 0) - (Number(a.totalSales) || 0))
            .reduce((map, c, i) => ({ ...map, [c.clientCmp]: i + 1 }), {});

        const profitRankMap = [...rawClientList]
            .sort((a, b) => (Number(b.totalProfit) || 0) - (Number(a.totalProfit) || 0))
            .reduce((map, c, i) => ({ ...map, [c.clientCmp]: i + 1 }), {});

        // 화면 표출을 위한 검색어 필터링 진행
        const filtered = rawClientList.filter(c => {
            const clientName = c.partnerNm || c.clientCmp || '';
            return clientName.toLowerCase().includes(searchTerm.toLowerCase());
        });

        // 필터링된 결과 내에서 사용자가 선택한 정렬 탭(정렬 키) 기준으로 최종 정렬
        return filtered
            .sort((a, b) => {
                const valA = Number(a[sortKey] || 0);
                const valB = Number(b[sortKey] || 0);
                return sortOrder === 'desc' ? valB - valA : valA - valB;
            })
            .map((c) => ({
                ...c, 
                salesRank: salesRankMap[c.clientCmp] || '-',
                profitRank: profitRankMap[c.clientCmp] || '-'
            }));
    }, [searchTerm, sortKey, sortOrder, rawClientList]);

    // 실시간 검색 상단 합계 금액 연산
    const searchSummarySummary = useMemo(() => {
        return processedClients.reduce((sum, c) => {
            sum.sales += Number(c.totalSales || 0);
            sum.profit += Number(c.totalProfit || 0);
            return sum;
        }, { sales: 0, profit: 0 });
    }, [processedClients]);

    if (!isOpen) return null;

    const thStyle = { padding: '8px 16px', fontWeight: '600', cursor: 'pointer', userSelect: 'none' };
    const tdStyle = { padding: '7px 16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

    const getSortIcon = (key) => {
        if (sortKey !== key) return ' ⬌';
        return sortOrder === 'desc' ? ' ▼' : ' ▲';
    };

    const renderRankBadge = (rank) => (
        <span style={{ fontSize: '11px', fontWeight: '700', color: typeof rank === 'number' && rank <= 3 ? '#2563eb' : '#94a3b8', marginLeft: '6px' }}>
            ({rank}위)
        </span>
    );

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, padding: '20px', fontFamily: 'sans-serif' }}>
            <div style={{ backgroundColor: '#ffffff', width: '720px', height: '650px', borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                
                {/* 헤더 */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: '3px', height: '14px', backgroundColor: '#4b5563' }}></span>
                        <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                            전사 고객사 검색 <span style={{ fontSize: '12px', fontWeight: '500', color: '#2563eb', marginLeft: '4px' }}>({searchYear}년도 데이터 기준)</span>
                        </h2>
                    </div>
                    <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '18px', color: '#94a3b8', cursor: 'pointer' }}>✕</button>
                </div>

                {/* 검색 필드 */}
                <div style={{ padding: '14px 20px 10px 20px', backgroundColor: '#ffffff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '10px', color: '#64748b', fontSize: '13px' }}>🔍</span>
                        <input type="text" placeholder="고객사명 검색..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: '100%', padding: '6px 12px 6px 30px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', color: '#334155' }} autoFocus />
                    </div>
                </div>

                {/* 검색 결과 분석 대역 배너 */}
                <div style={{ margin: '0 20px 10px 20px', padding: '8px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', display: 'flex', gap: '24px', fontSize: '12px', color: '#475569' }}>
                    <div>
                        <span style={{ fontWeight: '600', color: '#64748b' }}>[{searchYear}년] 총 매출액: </span>
                        <strong style={{ color: '#1e293b', fontSize: '13px' }}>₩{searchSummarySummary.sales.toLocaleString()}</strong>
                    </div>
                    <div style={{ width: '1px', height: '14px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                    <div>
                        <span style={{ fontWeight: '600', color: '#64748b' }}>[{searchYear}년] 총 매출 이익: </span>
                        <strong style={{ color: '#16a34a', fontSize: '13px' }}>₩{searchSummarySummary.profit.toLocaleString()}</strong>
                    </div>
                </div>

                {/* 데이터 그리드 */}
                <div style={{ flex: 1, overflowY: 'auto', borderTop: '1px solid #f1f5f9' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ position: 'sticky', top: 0, backgroundColor: '#e0f2fe', borderBottom: '1px solid #cbd5e1', color: '#374151', zIndex: 1 }}>
                                <th style={{ ...thStyle, cursor: 'default', width: '220px', paddingLeft: '20px' }}>고객사명</th>
                                <th onClick={() => handleSort('totalSales')} style={{ ...thStyle, textAlign: 'right', width: '180px' }}>매출액{getSortIcon('totalSales')}</th>
                                <th onClick={() => handleSort('totalProfit')} style={{ ...thStyle, textAlign: 'right', width: '180px' }}>매출 이익{getSortIcon('totalProfit')}</th>
                            </tr>
                        </thead>
                        <tbody style={{ color: '#4b5563' }}>
                            {processedClients.map((client) => (
                                <tr key={client.clientCmp} onClick={() => { onSelectClient(client); onClose(); }} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff', cursor: 'pointer' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}>
                                    <td style={{ ...tdStyle, fontWeight: '500', color: '#1f2937', paddingLeft: '20px' }}>{client.partnerNm || client.clientCmp || '미지정 고객사'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                                        ₩{(client.totalSales || 0).toLocaleString()}
                                        {renderRankBadge(client.salesRank)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a', fontWeight: '600' }}>
                                        ₩{(client.totalProfit || 0).toLocaleString()}
                                        {renderRankBadge(client.profitRank)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* 🎯 [완벽 복구] 하단 스팬 안내 문구 탑재 및 닫기 버튼 배치 정렬 */}
                <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: '#64748b', letterSpacing: '-0.3px' }}>
                        * 표시되는 순위는 선택한 연도의 전사 실적 금액을 기준으로 상시 고정 추적됩니다.
                    </span>
                    <button onClick={onClose} style={{ padding: '6px 14px', backgroundColor: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '12px', fontWeight: '500', color: '#475569', cursor: 'pointer', transition: 'all 0.1s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}>
                        닫기
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ClientSearchModal;
